import autosizeLib from 'autosize';

//

const getMaxLengthCounter = (value, maxLength) => maxLength - value.length;

/**
 * @figma https://www.figma.com/file/cBHD57QcCt9WDT4e7e2B3w/hse_general_library?node-id=4344%3A1
 * @storybook /?path=/story/inputs-textarea--playground
 * @react /src-components-textarea
 * @reactStorybook /?path=/story/inputs-textarea--playground
 */
var script = {
  name: 'HseTextArea',
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    /**
     * Значение счетчика в углу поля.
     * Может быть использован как счетчик оставшегося числа символов,
     * так и как счетчик введенного количества символов, слов и т.п.
     * Требуемое поведение счетчика необходимо реализовать самостоятельно
     * при использовании компонента
     */
    counter: {
      type: String
    },
    /**
     * Отображает счетчик красным цветом, чтобы сфокусировать на нём внимание
     * в случае нарушения ограничения
     */
    counterError: {
      type: Boolean
    },
    /**
     * В каком направлении разрешить изменение размера компонента.
     * Значения соответствуют css-свойству resize.
     * По-умолчанию позволяет изменение размера только по вертикали
     * только если autosize=false.
     * Не стоит задавать это свойство совместно с autosize=true.
     * Изменение размера по горизонтали не поддерживается, см. документацию.
     */
    resize: {
      type: String
    },
    /**
     * Текущее значение
     */
    value: {
      type: String
    },
    /**
     * Отключенное состояние
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Состояние ошибки
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * 100% ширина компонента
     */
    fullWidth: {
      type: Boolean,
      default: false
    },
    /**
     * Автоматический подбор высоты компонента
     */
    autosize: {
      type: Boolean,
      default: false
    },
    /**
     * Дополнительные классы элементов компонента:
     * - root - внешний div
     * - textArea - элемент textarea
     * - counter - счетчик в углу поля
     */
    classes: {
      type: Object,
      default: () => ({})
    },
    /**
     * Дополнительные пропы корневого элемента
     */
    rootProps: {
      type: Object
    },
    /**
     * Дополнительные стили элемента textarea
     */
    inputStyle: {
      type: Object
    },
    /**
     * Максимальное количество символов допустимое для ввода в текстовое поле.
     * По умолчанию включает отображения счетчика оставшихся символов.
     * Чтобы его отключить, передайте counter=false
     */
    maxlength: {
      type: Number
    }
  },
  data() {
    return {
      focused: false,
      valueState: this.value
    };
  },
  watch: {
    autosize(val) {
      if (val) {
        this.autosizeInit();
      } else {
        this.autosizeDestroy();
      }
    },
    value: {
      handler(value) {
        if (typeof value !== 'undefined') {
          this.valueState = value;
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.autosizeInit();
  },
  updated() {
    this.autosizeUpdate();
  },
  beforeDestroy() {
    this.autosizeDestroy();
  },
  computed: {
    maxLength() {
      return this.maxlength;
    },
    listeners() {
      return {
        ...this.$listeners,
        focus: this.onFocus,
        blur: this.onBlur,
        input: this.onInput
      };
    },
    counterValue() {
      if (typeof this.counter !== 'undefined') {
        return this.counter;
      } else if (typeof this.maxLength !== 'undefined') {
        return getMaxLengthCounter(this.valueState, this.maxLength);
      }
      return undefined;
    },
    hasCounter() {
      return (
        (typeof this.counterValue !== 'undefined' &&
          this.counterValue !== false) ||
        this.$slots.counter
      );
    },
    maxLengthError() {
      return (
        typeof this.counter === 'undefined' &&
        typeof this.maxLength !== 'undefined' &&
        typeof this.counterValue === 'number' &&
        this.counterValue < 0
      );
    },
    counterErrorValue() {
      return typeof this.counterError !== 'undefined'
        ? this.counterError
        : this.maxLengthError;
    }
  },
  methods: {
    autosizeInit() {
      if (this.autosize) {
        autosizeLib(this.$refs.textarea);
      }
    },
    autosizeUpdate() {
      if (this.autosize) {
        autosizeLib.update(this.$refs.textarea);
      }
    },
    autosizeDestroy() {
      autosizeLib.destroy(this.$refs.textarea);
    },
    onInput(e) {
      const $textarea = e.target;
      if (typeof this.value === 'undefined') {
        this.valueState = $textarea.value;
      }

      /**
       * Событие ввода
       * @type {string}
       */
      this.$emit('input', $textarea.value);
      this.$nextTick(() => {
        if ($textarea.value !== this.value) {
          $textarea.value = this.value;
        }
      });
    },
    onFocus(e) {
      this.focused = true;
      /**
       * Событие фокуса
       * @type {FocusEvent}
       */
      this.$emit('focus', e);
    },
    onBlur(e) {
      this.focused = false;
      /**
       * Событие потери фокуса
       * @type {FocusEvent}
       */
      this.$emit('blur', e);
    }
  }
};

export default script;
